"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

export enum AlertType {
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
}

interface AlertState {
  isVisible: boolean;
  type: AlertType;
  description: string;
  duration: number;
}

export interface AlertContextType {
  alert: AlertState;
  showAlert: (type: AlertType, description: string) => void;
  hideAlert: () => void;
}

export type { AlertState };

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlertContext must be used within an AlertProvider");
  }
  return context;
};

interface AlertProviderProps {
  children: React.ReactNode;
}

const AlertComponent: React.FC<{ alert: AlertState; onClose: () => void }> = ({
  alert,
  onClose,
}) => {
  if (!alert.isVisible) return null;

  const getAlertConfig = (type: AlertType) => {
    switch (type) {
      case AlertType.SUCCESS:
        return {
          title: "Success",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          iconColor: "text-green-600",
          progressColor: "bg-green-500",
          icon: "✓",
        };
      case AlertType.WARNING:
        return {
          title: "Warning",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          iconColor: "text-yellow-600",
          progressColor: "bg-yellow-500",
          icon: "⚠",
        };
      case AlertType.ERROR:
        return {
          title: "Error",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          iconColor: "text-red-600",
          progressColor: "bg-red-500",
          icon: "✕",
        };
    }
  };

  const config = getAlertConfig(alert.type);

  return (
    <div
      className="fixed top-4 right-4 z-50 max-w-sm w-full transform transition-all duration-300 ease-in-out"
      style={{
        transform: alert.isVisible ? "translateX(0)" : "translateX(100%)",
      }}
    >
      <div
        className={`${config.bgColor} ${config.borderColor} rounded-lg shadow-lg border p-4 relative`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors text-xl font-bold"
          aria-label="Close alert"
        >
          ×
        </button>

        {/* Alert content */}
        <div className="pr-8 flex items-start space-x-3">
          <div
            className={`${config.iconColor} text-xl font-bold flex-shrink-0 mt-0.5`}
          >
            {config.icon}
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${config.iconColor} mb-1`}>
              {config.title}
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {alert.description}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 bg-gray-200 rounded-full h-1 overflow-hidden">
          <div
            className={`${config.progressColor} h-full transition-all ease-linear`}
            style={{
              animation: `shrink ${alert.duration}s linear forwards`,
            }}
          />
        </div>

        <style jsx>{`
          @keyframes shrink {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<AlertState>({
    isVisible: false,
    type: AlertType.SUCCESS,
    description: "",
    duration: 5,
  });

  const hideAlert = useCallback(() => {
    setAlert((prev) => ({
      ...prev,
      isVisible: false,
    }));

    // Clean out data after hide animation completes
    setTimeout(() => {
      setAlert({
        isVisible: false,
        type: AlertType.SUCCESS,
        description: "",
        duration: 5,
      });
    }, 300);
  }, []);

  const getDurationForType = (type: AlertType): number => {
    switch (type) {
      case AlertType.SUCCESS:
        return 5;
      case AlertType.WARNING:
      case AlertType.ERROR:
        return 10;
    }
  };

  const showAlert = useCallback(
    (type: AlertType, description: string) => {
      const duration = getDurationForType(type);

      // If there's already an alert showing, hide it first
      if (alert.isVisible) {
        hideAlert();
        setTimeout(() => {
          setAlert({
            isVisible: true,
            type,
            description,
            duration,
          });
        }, 300);
      } else {
        setAlert({
          isVisible: true,
          type,
          description,
          duration,
        });
      }
    },
    [alert.isVisible, hideAlert]
  );

  // Auto-hide effect
  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        hideAlert();
      }, alert.duration * 1000);

      return () => clearTimeout(timer);
    }
  }, [alert.isVisible, alert.duration, hideAlert]);

  const contextValue: AlertContextType = {
    alert,
    showAlert,
    hideAlert,
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <AlertComponent alert={alert} onClose={hideAlert} />
    </AlertContext.Provider>
  );
};
